import { toast } from 'sonner';

import { DocumentDownloadOptions, ReferencesDict } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface ExportWithDocumentsResponse {
  exportId?: string;
  jobId?: string;
  inProgress?: boolean;
}

interface DownloadUrlResponse {
  downloadUrl: string;
}

export const useExportKimWithDocuments = (
  matterId?: string,
  subsetId?: string,
  threadId?: string,
  messageId?: string,
) => {
  const { fetchConfigGET, getFetchConfig } = useGetFetchConfig();

  const startExport = async (
    title: string,
    content: string,
    references?: ReferencesDict,
    options?: DocumentDownloadOptions,
  ): Promise<ExportWithDocumentsResponse> => {
    if (!matterId || !subsetId || !threadId || !messageId) {
      throw new Error('Missing required IDs');
    }

    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/message/${messageId}/export-with-documents`;
    const fetchConfig = getFetchConfig({
      method: FetchMethod.POST,
      data: {
        title,
        content,
        ...(references && { references }),
        ...(options && { options }),
      },
    });

    const response = await fetch(endpoint, fetchConfig);

    if (!response.ok) {
      toast.error('Failed to start Kim export');
      return {};
    }

    return response.json();
  };

  const downloadExport = async (exportId: string): Promise<void> => {
    if (!matterId || !subsetId || !threadId || !messageId) {
      toast.error('Missing required IDs for export');
      return;
    }

    const endpoint = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/message/${messageId}/export-download-url?exportId=${exportId}`;
    const response = await fetch(endpoint, fetchConfigGET);

    if (!response.ok) {
      toast.error('Failed to get Kim export download URL');
      return;
    }

    const data: DownloadUrlResponse = await response.json();

    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.download = 'Kim Answer with source documents.zip';
    link.click();
  };

  const getProgressEndpoint = (exportMessageId: string) => {
    return `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/message/${exportMessageId}/export-progress`;
  };

  return {
    startExport,
    downloadExport,
    getProgressEndpoint,
  };
};
